/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.3
 * Description: System form component with BIT/TPX management support
 */

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { System } from '@services/api'
import ProductsCatalog from '@components/ProductsCatalog'

interface SystemFormProps {
  system?: System
  onSubmit: (data: System) => void
  onCancel: () => void
  isLoading?: boolean
}

const SystemForm: React.FC<SystemFormProps> = ({ system, onSubmit, onCancel, isLoading = false }) => {
  const [activeTab, setActiveTab] = useState<'details' | 'products'>('details')
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<System>({
    mode: 'onChange',
    defaultValues: system || {},
  })

  useEffect(() => {
    if (system) {
      reset(system)
    }
  }, [system, reset])

  const handleFormSubmit = (data: System) => {
    // Convert checkbox values to proper booleans
    const formattedData = {
      ...data,
      shared_resources_non_cb: Boolean(data.shared_resources_non_cb),
      productIds: !system?.id ? selectedProductIds : undefined,
      complexity: data.complexity
        ? {
            ...data.complexity,
            code_owned: Boolean(data.complexity.code_owned),
            code_access: Boolean(data.complexity.code_access),
          }
        : undefined,
    }
    onSubmit(formattedData)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Tab Navigation */}
      <div className="overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`cursor-pointer border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            System Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('products')}
            className={`cursor-pointer border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'products'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            Products
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* System Name - Required */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              System Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('system_name', { required: 'System name is required' })}
              className="input-field"
              placeholder="Enter system name"
            />
            {errors.system_name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.system_name.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              {...register('description')}
              rows={3}
              className="input-field"
              placeholder="Enter system description"
            />
          </div>

          {/* Origin */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Origin</label>
            <input {...register('origin')} className="input-field" placeholder="System origin" />
          </div>

          {/* Placeholder for grid alignment */}
          <div></div>

          {/* User interface(s) today */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              User interface(s) today
            </label>
            <input {...register('user_interfaces')} className="input-field" placeholder="e.g., Web, Mobile, CLI" />
          </div>

          {/* Maintenance schedule */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Maintenance schedule
            </label>
            <input
              {...register('maintenance_schedule')}
              className="input-field"
              placeholder="e.g., Monthly, Quarterly"
            />
          </div>

          {/* Business owner */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Business owner</label>
            <input {...register('business_owner')} className="input-field" placeholder="Business owner name" />
          </div>

          {/* Technical product owner */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Technical product owner
            </label>
            <input
              {...register('technical_product_owner')}
              className="input-field"
              placeholder="Technical owner name"
            />
          </div>

          {/* Placeholder to maintain grid alignment */}
          <div></div>

          {/* Sales Channels */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Sales Channels / Segments
            </label>
            <input
              {...register('sales_channels')}
              className="input-field"
              placeholder="e.g., SMB, Indirect, Enterprise, Custom Enterprise"
            />
          </div>

          {/* Customers impacted */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              What customers are impacted
            </label>
            <textarea {...register('customers_impacted')} rows={2} className="input-field" />
          </div>

          {/* Volume (monthly) */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Volume (monthly)</label>
            <input {...register('monthly_volume')} className="input-field" placeholder="Transactional volume" />
          </div>

          {/* Customer footprint */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Customer footprint
            </label>
            <input {...register('customer_footprint')} className="input-field" />
          </div>

          {/* Product Related Notes */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Product related notes
            </label>
            <input {...register('product_related_notes')} className="input-field" />
          </div>

          {/* Other user groups */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Other user groups outside of sales
            </label>
            <input {...register('other_user_groups')} className="input-field" />
          </div>

          {/* Site capability */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Site capability</label>
            <input {...register('site_capability')} className="input-field" />
          </div>

          {/* Placeholder to maintain grid alignment */}
          <div></div>

          {/* Limitations */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Limitations</label>
            <textarea {...register('limitations')} rows={3} className="input-field" />
          </div>

          {/* Integration capabilities */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Integration capabilities
            </label>
            <textarea {...register('integration_capabilities')} rows={3} className="input-field" />
          </div>

          {/* Dependencies */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Dependencies</label>
            <textarea {...register('dependencies')} rows={3} className="input-field" />
          </div>

          {/* Impacted subsystems */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Impacted sub systems
            </label>
            <textarea {...register('impacted_subsystems')} rows={3} className="input-field" />
          </div>

          {/* NorthStar Alignment */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              NorthStar Alignment
            </label>
            <textarea {...register('northstar_alignment')} rows={3} className="input-field" />
          </div>

          {/* Divider for dropdown section */}
          <div className="mt-2 border-t border-gray-300 pt-4 md:col-span-2 dark:border-gray-600">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Classification & Scores</h3>
          </div>

          {/* Development Origin */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Development Origin
            </label>
            <div className="space-y-2">
              {[
                { value: 'acquisition', label: 'Via Acquisition' },
                { value: 'homegrown', label: 'CB Homegrown' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('acquisition_type')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Custom vs. Vendor Partnership vs. Off the shelf */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom vs. Vendor Partnership vs. Off the shelf
            </label>
            <div className="space-y-2">
              {[
                { value: 'custom', label: 'Custom' },
                { value: 'vendor_partnership', label: 'Vendor Partnership' },
                { value: 'off_the_shelf', label: 'Off the Shelf' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('custom_type')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* BIT vs. CB Managed */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              BIT vs. CB Managed
            </label>
            <div className="space-y-2">
              {[
                { value: 'BIT', label: 'BIT/TPX Managed' },
                { value: 'CB', label: 'CB Managed' },
                { value: 'shared', label: 'Shared' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('management_type')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* International vs. Domestic */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              International vs. Domestic
            </label>
            <div className="space-y-2">
              {[
                { value: 'international', label: 'International' },
                { value: 'domestic', label: 'Domestic' },
                { value: 'both', label: 'Both' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('geographic_scope')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Security score */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">Security score</label>
            <div className="space-y-2">
              {[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('security_score')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Compliance score */}
          <div>
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">Compliance score</label>
            <div className="space-y-2">
              {[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ].map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    value={option.value}
                    {...register('compliance_score')}
                    className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Shared resources with non-CB */}
          <div className="md:col-span-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('shared_resources_non_cb')}
                className="text-primary-600 focus:ring-primary-500 rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Shared resources with non-CB Comcast Organizations
              </span>
            </label>
          </div>

          {/* Divider for complexity section */}
          <div className="mt-2 border-t border-gray-300 pt-4 md:col-span-2 dark:border-gray-600">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">System Complexity</h3>
          </div>

          {/* Code Owner */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Code owner</label>
            <input
              {...register('complexity.code_owner')}
              className="input-field"
              placeholder="Email contact for code ownership"
            />
          </div>

          {/* API Documentation */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">API documentation</label>
            <input
              {...register('complexity.api_documentation')}
              className="input-field"
              placeholder="API documentation URL"
            />
          </div>

          {/* Code Owned */}
          <div>
            <label className="flex cursor-pointer items-center space-x-2">
              <input
                type="checkbox"
                {...register('complexity.code_owned')}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Code owned by Comcast</span>
            </label>
          </div>

          {/* Code Access */}
          <div>
            <label className="flex cursor-pointer items-center space-x-2">
              <input
                type="checkbox"
                {...register('complexity.code_access')}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Able to access code</span>
            </label>
          </div>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="py-4">
          <ProductsCatalog
            systemId={system?.id}
            systemName={system?.system_name || 'New System'}
            onProductsChange={!system?.id ? setSelectedProductIds : undefined}
            selectedProductIds={selectedProductIds}
          />
        </div>
      )}

      {/* Form Actions - only visible in details tab */}
      {(activeTab === 'details' || !system) && (
        <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
          <button type="button" onClick={onCancel} className="btn-secondary cursor-pointer" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary cursor-pointer" disabled={isLoading}>
            {isLoading ? 'Saving...' : system?.id ? 'Update System' : 'Create System'}
          </button>
        </div>
      )}
    </form>
  )
}

export default SystemForm
